import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import {
  ArrowLeft,
  Search,
  Filter,
  Download,
  CheckCircle2,
  Clock,
  Play,
  Eye,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { getStudentSession } from '../../utils/supabase/auth';
import { projectId, publicAnonKey } from '../../utils/supabase/info';

interface LessonGrade {
  id: string;
  title: string;
  subject: string;
  mode: 'live' | 'student-paced';
  completion: number;
  score: number | null;
  status: 'completed' | 'in-progress' | 'awaiting-grade';
  completedAt?: Date;
  activitiesTotal: number;
  activitiesCompleted: number;
}

export default function StudentGrades() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [sortBy, setSortBy] = useState<'date' | 'score' | 'title'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [lessons, setLessons] = useState<LessonGrade[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load student grades on mount
  useEffect(() => {
    loadStudentGrades();
  }, []);

  const loadStudentGrades = async () => {
    try {
      setLoading(true);
      setError(null);
      const studentSession = getStudentSession();
      if (!studentSession) {
        setError('Please join a session first');
        setLoading(false);
        return;
      }

      console.log('Loading grades for student:', studentSession.studentName);

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-9d7d881a/student/grades`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${publicAnonKey}`
          },
          body: JSON.stringify({
            fullName: studentSession.studentName
          })
        }
      );

      const payload = await response.json();
      
      if (!response.ok) {
        console.error('Failed to load grades:', payload);
        throw new Error(payload.error || 'Failed to load grades');
      }

      console.log('Grades response:', payload);

      const gradesData = payload.grades || [];
      const lessonsMap = new Map<string, { id: string; title: string; subject: string; mode: 'live' | 'student-paced' }>(
        (payload.lessons || []).map((lesson: any) => [lesson.id, lesson])
      );

      // Use server-provided slide/submission counts (no direct Supabase queries needed)
      const slidesByLesson: Record<string, number> = payload.slidesByLesson || {};
      const submissionsByLesson: Record<string, number> = payload.submissionsByLesson || {};

      // For each grade, get the lesson details
      if (gradesData && gradesData.length > 0) {
        const lessonsData = gradesData.map((grade: { lesson_id: unknown; total_score: number; max_possible_score: any; completion_percentage: number; id: any; updated_at: string | number | Date; }) => {
          const lessonId = String(grade.lesson_id);
          const lessonData = lessonsMap.get(lessonId);

          const score = Math.round(
            (grade.total_score / (grade.max_possible_score || 100)) * 100
          );
          let status: 'completed' | 'in-progress' | 'awaiting-grade' = 'completed';
          if (grade.completion_percentage < 100) {
            status = 'in-progress';
          }

          return {
            id: grade.id,
            title: lessonData?.title || 'Unnamed Lesson',
            subject: lessonData?.subject || 'General',
            mode: (lessonData?.mode || 'live') as 'live' | 'student-paced',
            completion: grade.completion_percentage || 0,
            score: score || null,
            status,
            completedAt: grade.updated_at ? new Date(grade.updated_at) : undefined,
            activitiesTotal: slidesByLesson[lessonId] || 0,
            activitiesCompleted: submissionsByLesson[lessonId] || 0,
          };
        });

        setLessons(lessonsData);
      } else {
        console.log('No grades found for this student');
        setLessons([]);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error loading student grades:', err);
      setError('Failed to load grades');
      toast.error('Failed to load your grades');
      setLoading(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600 bg-green-100 border-green-200';
    if (score >= 80) return 'text-emerald-600 bg-emerald-100 border-emerald-200';
    if (score >= 70) return 'text-orange-600 bg-orange-100 border-orange-200';
    if (score >= 60) return 'text-amber-600 bg-amber-100 border-amber-200';
    return 'text-red-600 bg-red-100 border-red-200';
  };

  const getStatusBadge = (status: LessonGrade['status']) => {
    switch (status) {
      case 'completed':
        return (
          <Badge className="bg-green-100 text-green-700 border-green-200 rounded-lg">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Completed
          </Badge>
        );
      case 'in-progress':
        return (
          <Badge className="bg-blue-100 text-blue-700 border-blue-200 rounded-lg">
            <Play className="w-3 h-3 mr-1" />
            In Progress
          </Badge>
        );
      case 'awaiting-grade':
        return (
          <Badge className="bg-orange-100 text-orange-700 border-orange-200 rounded-lg">
            <Clock className="w-3 h-3 mr-1" />
            Awaiting Grade
          </Badge>
        );
    }
  };

  const availableSubjects = Array.from(
    new Set(lessons.map((lesson) => lesson.subject).filter(Boolean))
  );
  const showSubjectFilter = availableSubjects.length > 0;

  const filteredAndSortedLessons = lessons
    .filter(lesson => {
      const matchesSearch = lesson.title.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesSubject =
        !showSubjectFilter || selectedSubject === 'all' || lesson.subject === selectedSubject;
      return matchesSearch && matchesSubject;
    })
    .sort((a, b) => {
      let comparison = 0;
      
      if (sortBy === 'date') {
        const dateA = a.completedAt?.getTime() || 0;
        const dateB = b.completedAt?.getTime() || 0;
        comparison = dateB - dateA;
      } else if (sortBy === 'score') {
        comparison = (b.score || 0) - (a.score || 0);
      } else if (sortBy === 'title') {
        comparison = a.title.localeCompare(b.title);
      }
      
      return sortOrder === 'asc' ? -comparison : comparison;
    });

  const scoredLessons = lessons.filter(l => l.score !== null);
  const averageScore = scoredLessons.length > 0
    ? Math.round(scoredLessons.reduce((sum, l) => sum + (l.score || 0), 0) / scoredLessons.length)
    : 0;

  const toggleSort = (newSortBy: 'date' | 'score' | 'title') => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('desc');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {loading && (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading your grades...</p>
          </div>
        </div>
      )}

      {error && (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">{error}</h2>
            <Button onClick={() => navigate('/student/dashboard')}>
              Back to Dashboard
            </Button>
          </div>
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Header */}
          <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between mb-3 sm:mb-4 gap-3">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
              <Button
                variant="ghost"
                onClick={() => navigate('/student/dashboard')}
                className="rounded-xl p-2 sm:p-2"
                size="sm"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-semibold text-foreground truncate">My Grades</h1>
                <p className="text-xs sm:text-sm text-muted-foreground truncate">View all your lesson scores</p>
              </div>
            </div>
            <Button variant="outline" className="rounded-xl text-xs sm:text-sm p-2 sm:px-4" size="sm">
              <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Export</span>
            </Button>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search lessons..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 rounded-xl text-sm"
              />
            </div>

            {showSubjectFilter && (
              <Select value={selectedSubject} onValueChange={setSelectedSubject}>
                <SelectTrigger className="rounded-xl text-sm">
                  <SelectValue placeholder="All Subjects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Subjects</SelectItem>
                  {availableSubjects.map((subject) => (
                    <SelectItem key={subject} value={subject}>
                      {subject}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
              <SelectTrigger className="rounded-xl text-sm">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date">Sort by Date</SelectItem>
                <SelectItem value="score">Sort by Score</SelectItem>
                <SelectItem value="title">Sort by Title</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Summary Card */}
        <div className="bg-gradient-to-br from-primary/10 to-accent/10 border border-border rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-muted-foreground">Average Score</p>
                <p className="text-xl sm:text-2xl font-bold text-foreground">{averageScore}%</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-muted-foreground">Completed</p>
                <p className="text-xl sm:text-2xl font-bold text-foreground">
                  {lessons.filter(l => l.status === 'completed').length}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Play className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-muted-foreground">In Progress</p>
                <p className="text-xl sm:text-2xl font-bold text-foreground">
                  {lessons.filter(l => l.status === 'in-progress').length}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-muted-foreground">Awaiting</p>
                <p className="text-xl sm:text-2xl font-bold text-foreground">
                  {lessons.filter(l => l.status === 'awaiting-grade').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Grades Table - Desktop */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden hidden lg:block">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-6 py-4 text-left">
                    <button
                      onClick={() => toggleSort('title')}
                      className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Lesson Title
                      {sortBy === 'title' && (
                        sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-muted-foreground">
                    Subject
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-muted-foreground">
                    Mode
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-muted-foreground">
                    Completion
                  </th>
                  <th className="px-6 py-4 text-left">
                    <button
                      onClick={() => toggleSort('score')}
                      className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Score
                      {sortBy === 'score' && (
                        sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-muted-foreground">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-muted-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredAndSortedLessons.map((lesson) => (
                  <tr key={lesson.id} className="hover:bg-accent/5 transition-colors">
                    <td className="px-6 py-4">
                      <p className="font-medium text-foreground">{lesson.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {lesson.activitiesCompleted}/{lesson.activitiesTotal} activities
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant="outline" className="rounded-lg">
                        {lesson.subject || 'Unassigned'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <Badge
                        className={`rounded-lg ${
                          lesson.mode === 'live'
                            ? 'bg-red-100 text-red-700 border-red-200'
                            : 'bg-blue-100 text-blue-700 border-blue-200'
                        }`}
                      >
                        {lesson.mode === 'live' ? 'Live' : 'Student-Paced'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <div className="w-24">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium">{lesson.completion}%</span>
                        </div>
                        <Progress value={lesson.completion} className="h-2" />
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {lesson.score !== null ? (
                        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg border font-semibold ${getScoreColor(lesson.score)}`}>
                          {lesson.score}%
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(lesson.status)}
                    </td>
                    <td className="px-6 py-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/student/lesson/${lesson.id}`)}
                        className="rounded-lg"
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Grades Cards - Mobile & Tablet */}
        <div className="space-y-3 sm:space-y-4 lg:hidden">
          {filteredAndSortedLessons.map((lesson) => (
            <div
              key={lesson.id}
              className="bg-card border border-border rounded-2xl p-4 sm:p-6 cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => navigate(`/student/lesson/${lesson.id}`)}
            >
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm sm:text-base truncate">{lesson.title}</h3>
                  <p className="text-xs text-muted-foreground">
                    {lesson.activitiesCompleted}/{lesson.activitiesTotal} activities
                  </p>
                </div>
                {lesson.score !== null ? (
                  <div className={`text-right font-semibold text-lg ${getScoreColor(lesson.score)}`}>
                    {lesson.score}%
                  </div>
                ) : (
                  <Badge className="text-xs">-</Badge>
                )}
              </div>

              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Badge variant="outline" className="text-xs rounded-lg">
                  {lesson.subject || 'Unassigned'}
                </Badge>
                <Badge
                  className={`text-xs rounded-lg ${
                    lesson.mode === 'live'
                      ? 'bg-red-100 text-red-700 border-red-200'
                      : 'bg-blue-100 text-blue-700 border-blue-200'
                  }`}
                >
                  {lesson.mode === 'live' ? 'Live' : 'Student-Paced'}
                </Badge>
                {getStatusBadge(lesson.status)}
              </div>

              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium">Completion</span>
                  <span className="text-xs text-muted-foreground">{lesson.completion}%</span>
                </div>
                <Progress value={lesson.completion} className="h-2" />
              </div>

              <Button
                size="sm"
                variant="outline"
                onClick={(e) => { e.stopPropagation(); navigate(`/student/lesson/${lesson.id}`); }}
                className="rounded-lg w-full text-xs sm:text-sm"
              >
                <Eye className="w-3 h-3 mr-1" />
                View Details
              </Button>
            </div>
          ))}
        </div>

        {/* Grade Legend */}
        <div className="mt-6 sm:mt-8 bg-card border border-border rounded-2xl p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4">Grade Scale</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-6 h-6 sm:w-8 sm:h-8 rounded bg-green-600 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-semibold">90-100%</p>
                <p className="text-xs text-muted-foreground">Excellent</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-6 h-6 sm:w-8 sm:h-8 rounded bg-emerald-600 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-semibold">80-89%</p>
                <p className="text-xs text-muted-foreground">Good</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-6 h-6 sm:w-8 sm:h-8 rounded bg-orange-600 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-semibold">70-79%</p>
                <p className="text-xs text-muted-foreground">Fair</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-6 h-6 sm:w-8 sm:h-8 rounded bg-amber-600 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-semibold">60-69%</p>
                <p className="text-xs text-muted-foreground">Passing</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-6 h-6 sm:w-8 sm:h-8 rounded bg-red-600 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-semibold">Below 60%</p>
                <p className="text-xs text-muted-foreground">Needs Work</p>
              </div>
            </div>
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
}
